import React from 'react';
import { EntityId } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { destroyReport, updateReport } from "../../../../actions/sticker_maker/reportsActions";
import { ReportInterface, selectAllReports } from "../../../../store/sticker_maker/reportsSlice";
import { selectUserEntities } from "../../../../store/sticker_maker/usersSlice";
import { selectStickerPackEntities } from "../../../../store/sticker_maker/stickerPacksSlice";
import { formattedDate } from "../../../../helpers/DateHelpers";

import { message, Space, Tag } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { Button, ButtonDestroy } from "../../../../componets/Buttons";
import UserMeta from "../../Users/_partials/_Meta";
import StickerPackMeta from "../../StickerPacks/_partials/_Meta";

import Table from "../../../../componets/Table";

interface propsInterface {
  limit: number;
}

const REASON_TAGS: { [key: string]: string } = {
  1: 'red',
  2: 'blue',
  3: 'geekblue',
  4: 'orange'
};

const _List = ({ limit }: propsInterface) => {
  const navigate      = useNavigate();
  const dispatch      = useAppDispatch();
  const reports       = useAppSelector(selectAllReports);
  const users         = useAppSelector(selectUserEntities);
  const stickerPacks  = useAppSelector(selectStickerPackEntities);
  const count         = useAppSelector(state => state.stickerMaker.reports.count);
  const error         = useAppSelector(state => state.stickerMaker.reports.error);
  const status        = useAppSelector(state => state.stickerMaker.reports.status);

  function onClickDestroy(id: EntityId) {
    handleDestroy(id).catch(console.error);
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyReport(id));

    if (destroyReport.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      setTimeout(() => navigate(0), 1000);
    }

    if (destroyReport.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  function onToggleClose(report: ReportInterface) {
    dispatch(updateReport({ id: report.id, data: { closed: !report.closed } }));
  }

  const CloseReportBtn = ({ report }: { report: ReportInterface }) => {
    if (report.closed) {
      return <Button key='button-close' onClick={() => onToggleClose(report)} title='Open' icon={UnlockOutlined} />
    } else {
      return <Button key='button-close' onClick={() => onToggleClose(report)} title='Close' icon={LockOutlined} />
    }
  }

  const columns: ColumnsType<ReportInterface> = [
    {
      key: 'closed',
      title: 'Status',
      dataIndex: 'closed',
      render: (_, { closed }) => {
        if (closed) {
          return <Tag style={{ fontSize: 10 }} color='#b7c0cd'>CLOSED</Tag>;
        } else {
          return <Tag style={{ fontSize: 10 }} color='#fd9a18'>OPEN</Tag>;
        }
      },
      width: 90
    },

    {
      key: 'sticker_pack_id',
      title: 'Sticker pack',
      dataIndex: 'sticker_pack_id',
      render: (_, { sticker_pack_id }) => {
        return <StickerPackMeta stickerPack={stickerPacks[sticker_pack_id]} size='small' />
      },
      ellipsis: true
    },

    {
      key: 'user_id',
      title: 'Created by',
      dataIndex: 'user_id',
      render: (_, { active_user_id }) => {
        return <UserMeta user={users[active_user_id]} size='small' shape='circle' />;
      },
      ellipsis: true
    },

    Table.EXPAND_COLUMN,

    {
      key: 'reason_id',
      title: 'Reason',
      dataIndex: 'reason_id',
      render: (_, { reason_id, reason }) => {
        const colorTag = REASON_TAGS[reason_id];
        return <Tag color={colorTag}>{reason.toUpperCase()}</Tag>;
      },
    },

    {
      key: 'created_at',
      title: 'Created at',
      render: (_, { created_at }) => {
        return <small>{formattedDate(created_at).dateTime}</small>;
      },
      width: 200
    },

    {
      key: 'action',
      title: 'Action',
      render: (_, record) => {
        return (
          <Space size="middle">
            <CloseReportBtn report={record} />
            <ButtonDestroy key='button-destroy' onClick={() => onClickDestroy(record.id)} />
          </Space>
        );
      },
      width: 250
    },
  ];

  if (status === 'failed') {
    return <div>Error: {error}</div>
  }

  return (
    <>
      <Table
        rowKey='id'
        loading={status === 'loading'}
        columns={columns}
        dataSource={reports}
        pagination={{
          className: 'v-mt-15',
          align: 'right',
          total: count,
          size: limit
        }}
        expandable={{
          expandedRowRender: record => <span><strong>Reason Comment:</strong> {record.reason_text}</span>,
          rowExpandable: record => !!record.reason_text,
        }}
      />
    </>
  );
}

export default _List;
